// Auth plugin.
import * as actionTypes from '../interface/actionTypes'
import * as actions from '../interface/actions'
import { getAuthConfig } from '../interface/selectors'
import { subscribe } from '../subscription/requests'

import { disconnect } from '../oldLink/sagas'

// Other plugins.
import { connectWebsocket } from '../../connectivity/interface/effects'
import BasicError, { authCodes } from '../../errors'
import { logManager } from '../../logs'

// Libraries.
import { call, put, select, take } from 'redux-saga/effects'

// Constants
import { platforms } from '../../constants'

const log = logManager.getLogger('AUTH')

/**
 * Handles anonymous subscription and establishing the websocket.
 * Mocks the connection process using the auth actions to trigger required
 *      side-effects elsewhere.
 * @method anonymousConnect
 * @param  {Object} action A CONNECT action.
 * @return {Object} Response or error object.
 */
export function * anonymousConnect () {
  while (true) {
    const action = yield take(actionTypes.CONNECT)

    // Retrieve the connection info.
    const config = yield select(getAuthConfig)

    const requestOptions = {}
    const { credentials } = action.payload
    if (credentials.realm) {
      requestOptions.queryParams = {
        tokenrealm: credentials.realm
      }
    }

    const subscriptionInfo = {
      ...config.subscription,
      isAnonymous: true,
      // TODO: Hardcode / override this here?
      service: ['callMe']
    }

    log.info('Subscribing to callMe service.')
    const response = yield call(subscribe, subscriptionInfo, credentials, requestOptions)

    if (response.error) {
      log.info('Subscription failed. Error: ', response.error)
      // Subscription failed.
      yield put(actions.connectFinished(response, platforms.LINK))
      continue
    } else if (!response.subscriptionParams.service.includes('callMe')) {
      // Subscription was successful, but didn't include the callMe service.
      log.info('Subscription failed. Call Me service not provided with subscription.')
      yield put(
        actions.connectFinished({
          error: new BasicError({
            message: 'Subscription failed to receive required service: callMe',
            code: authCodes.MISSING_SERVICE
          })
        })
      )
      continue
    }

    log.info('Successfully subscribed to callMe service. Connecting to websocket.')

    // Connect the websocket.
    const websocketInfo = {
      ...config.websocket,
      url: response.subscriptionParams.notificationChannel
    }

    // Request the websocket connection.
    const wsResponse = yield connectWebsocket(websocketInfo, platforms.LINK)

    if (wsResponse.error) {
      log.info('Failed to connect to websocket. Error: ', wsResponse.error)
      // Websocket failed to connect.
      yield put(
        actions.connectFinished(
          {
            error: wsResponse.payload
          },
          platforms.LINK
        )
      )
      continue
    }
    log.info('Successfully connected to websocket.')

    yield put(
      actions.connectFinished(
        {
          userInfo: {
            username: action.payload.credentials.anonUsername
          },
          subscription: {
            ...response.subscriptionParams,
            url: response.subscription
          },
          connection: {
            server: {
              ...config.subscription
            },
            username: action.payload.credentials.username,
            requestOptions,
            // Store the fact that this is an anonymous subscription as part of
            //      connection state.
            isAnonymous: true,
            realm: action.payload.credentials.realm
          }
        },
        platforms.LINK
      )
    )
  }
}

/**
 * Entry saga for disconnection.
 * Anonymous disconnect is no different than regular disconnect,
 *      so re-use the regular logic from link.
 * @method anonymousDisconnect
 */
export function * anonymousDisconnect () {
  while (true) {
    yield take(actionTypes.DISCONNECT)

    log.info('Unsubscribing from callMe services.')
    // Call the link disconnect saga.
    yield call(disconnect)
  }
}
